/* global Astronomy */
/*
 * Rave Mandala — interactive planet-positions widget.
 *
 * Adapted from the standalone planet-positions prototype. The astronomy/HD
 * logic (gate sequence, gate->angle mapping, true lunar node, position calc)
 * is reused verbatim; the rendering is rewritten to be a contained,
 * brand-themed, theme-aware widget that mounts into every [data-mandala].
 */
(function () {
  "use strict";

  // This page's language, from /docs/i18n/<lang>.js (written by build_docs). The
  //64 gate names below are deliberately NOT translated: gate proper names stay
  // English in every locale (i18n/README.md's glossary), the same rule the chat
  // and the bodygraph follow. Planet names and the widget's own controls do
  // translate — they are ordinary words, not Human Design vocabulary.
  const T =
    window.GHD_DOCS_T ||
    function (key, fallback, vars) {
      return vars ? String(fallback).replace(/\{(\w+)\}/g, (m, k) => (vars[k] == null ? m : vars[k])) : fallback;
    };

  // --- Constants & Data ---------------------------------------------------
  const GATE_SEQUENCE = [
    41, 19, 13, 49, 30, 55, 37, 63, 22, 36, 25, 17, 21, 51, 42, 3, 27, 24, 2, 23, 8, 20, 16, 35, 45, 12, 15, 52, 39, 53,
    62, 56, 31, 33, 7, 4, 29, 59, 40, 64, 47, 6, 46, 18, 48, 57, 32, 50, 28, 44, 1, 43, 14, 34, 9, 5, 26, 11, 10, 58,
    38, 54, 61, 60,
  ];

  const GATE_NAMES = {
    1: "Self-Expression",
    2: "Direction of Self",
    3: "Ordering",
    4: "Formulization",
    5: "Fixed Rhythms",
    6: "Friction",
    7: "Role of the Self",
    8: "Contribution",
    9: "Focus",
    10: "Behavior of the Self",
    11: "Ideas",
    12: "Caution",
    13: "The Listener",
    14: "Power Skills",
    15: "Extremes",
    16: "Skills",
    17: "Opinions",
    18: "Correction",
    19: "Wanting",
    20: "The Now",
    21: "The Hunter/Huntress",
    22: "Openness",
    23: "Assimilation",
    24: "Rationalizing",
    25: "Spirit of Self",
    26: "The Egotist",
    27: "Caring",
    28: "The Game Player",
    29: "Saying Yes",
    30: "Recognition of Feelings",
    31: "Leading",
    32: "Continuity",
    33: "Privacy",
    34: "Might",
    35: "Change",
    36: "Crisis",
    37: "Friendship",
    38: "The Fighter",
    39: "The Provocateur",
    40: "Aloneness",
    41: "Contraction",
    42: "Growth",
    43: "Insight",
    44: "Alertness",
    45: "Gatherer",
    46: "Determination of the Self",
    47: "Realizing",
    48: "Depth",
    49: "Revolution",
    50: "Values",
    51: "Shock",
    52: "Stillness",
    53: "Beginnings",
    54: "Ambition",
    55: "Spirit",
    56: "Stimulation",
    57: "Intuition",
    58: "Aliveness",
    59: "Sexuality",
    60: "Acceptance",
    61: "Mystery",
    62: "Detail",
    63: "Doubt",
    64: "Confusion",
  };

  // Planet dot colors chosen to read on both light and dark backgrounds.
  // `id` stays English — it is the key the astronomy engine and the search box
  // match on. `label` is what the reader sees, so the two nodes keep their short
  // forms and every body gets its name in the page's language.
  const PLANETS = [
    { id: "Sun", label: T("chart.planet.sun", "Sun"), symbol: "☉", color: "#E8A317" },
    { id: "Earth", label: T("chart.planet.earth", "Earth"), symbol: "⊕", color: "#3E8ED0" },
    { id: "NorthNode", label: T("chart.planet.northnode_short", "N.Node"), symbol: "☊", color: "#3FA66A" },
    { id: "SouthNode", label: T("chart.planet.southnode_short", "S.Node"), symbol: "☋", color: "#3FA66A" },
    { id: "Moon", label: T("chart.planet.moon", "Moon"), symbol: "☾", color: "#9AA0A6" },
    { id: "Mercury", label: T("chart.planet.mercury", "Mercury"), symbol: "☿", color: "#7E8A93" },
    { id: "Venus", label: T("chart.planet.venus", "Venus"), symbol: "♀", color: "#D6669A" },
    { id: "Mars", label: T("chart.planet.mars", "Mars"), symbol: "♂", color: "#D6452B" },
    { id: "Jupiter", label: T("chart.planet.jupiter", "Jupiter"), symbol: "♃", color: "#D98A2B" },
    { id: "Saturn", label: T("chart.planet.saturn", "Saturn"), symbol: "♄", color: "#B08338" },
    { id: "Uranus", label: T("chart.planet.uranus", "Uranus"), symbol: "♅", color: "#2AA7A0" },
    { id: "Neptune", label: T("chart.planet.neptune", "Neptune"), symbol: "♆", color: "#4A6FD0" },
    { id: "Pluto", label: T("chart.planet.pluto", "Pluto"), symbol: "♇", color: "#A467C4" },
  ];

  const GATE_ANGLE = 360 / 64; // 5.625 degrees
  // Gate 41 (index 0) starts at 2°00' Aquarius = 302° ecliptic longitude
  const GATE_41_START = 302;

  // Human Design channels: pairs of gates that form a complete channel
  const CHANNELS = [
    [1, 8],
    [2, 14],
    [3, 60],
    [4, 63],
    [5, 15],
    [6, 59],
    [7, 31],
    [9, 52],
    [10, 20],
    [10, 34],
    [10, 57],
    [11, 56],
    [12, 22],
    [13, 33],
    [16, 48],
    [17, 62],
    [18, 58],
    [19, 49],
    [20, 34],
    [20, 57],
    [21, 45],
    [23, 43],
    [24, 61],
    [25, 51],
    [26, 44],
    [27, 50],
    [28, 38],
    [29, 46],
    [30, 41],
    [32, 54],
    [34, 57],
    [35, 36],
    [37, 40],
    [39, 55],
    [42, 53],
    [47, 64],
  ];

  // --- Pure helpers (defined before use) ----------------------------------
  function gateToAngleDeg(gateNum) {
    const idx = GATE_SEQUENCE.indexOf(gateNum);
    return GATE_41_START + idx * GATE_ANGLE + GATE_ANGLE / 2;
  }

  function degToCanvasRad(deg) {
    return (-deg * Math.PI) / 180;
  }

  // True (osculating) lunar node longitude (Meeus, Astronomical Algorithms).
  // Human Design uses the true node, not the mean node.
  function trueLunarNodeLon(date) {
    const jd = 2440587.5 + date.getTime() / 86400000;
    const jc = (jd - 2451545.0) / 36525;
    const rad = Math.PI / 180;

    let omega = 125.04452 - 1934.136261 * jc + 0.0020708 * jc * jc + (jc * jc * jc) / 450000;

    const D = (297.85036 + 445267.11148 * jc - 0.0019142 * jc * jc + (jc * jc * jc) / 189474) * rad;
    const M = (357.52772 + 35999.05034 * jc - 0.0001603 * jc * jc - (jc * jc * jc) / 300000) * rad;
    const Mp = (134.96298 + 477198.867398 * jc + 0.0086972 * jc * jc + (jc * jc * jc) / 56250) * rad;
    const F = (93.27191 + 483202.017538 * jc - 0.0036825 * jc * jc + (jc * jc * jc) / 327270) * rad;

    omega +=
      -1.4979 * Math.sin(2 * (D - F)) -
      0.15 * Math.sin(M) -
      0.1226 * Math.sin(2 * D) +
      0.1176 * Math.sin(2 * F) -
      0.0801 * Math.sin(2 * (Mp - F));

    return ((omega % 360) + 360) % 360;
  }

  function lonToGateLine(lon) {
    let diff = lon - GATE_41_START;
    if (diff < 0) diff += 360;

    const gateIndex = Math.floor(diff / GATE_ANGLE) % 64;
    const gate = GATE_SEQUENCE[gateIndex];

    const withinGate = diff - gateIndex * GATE_ANGLE;
    let lineNum = Math.floor(withinGate / (GATE_ANGLE / 6)) + 1;
    if (lineNum > 6) lineNum = 6;
    if (lineNum < 1) lineNum = 1;

    return { gate, line: lineNum };
  }

  function calculatePositions(date) {
    if (typeof Astronomy === "undefined") return [];

    const out = [];
    PLANETS.forEach((planet) => {
      try {
        const time = Astronomy.MakeTime(date);
        let lon;

        if (planet.id === "Sun") {
          lon = Astronomy.SunPosition(time).elon;
        } else if (planet.id === "Earth") {
          lon = (Astronomy.SunPosition(time).elon + 180) % 360;
        } else if (planet.id === "Moon") {
          lon = Astronomy.EclipticGeoMoon(time).lon;
        } else if (planet.id === "NorthNode") {
          lon = trueLunarNodeLon(date);
        } else if (planet.id === "SouthNode") {
          lon = (trueLunarNodeLon(date) + 180) % 360;
        } else {
          const geo = Astronomy.GeoVector(planet.id, time, true);
          lon = Astronomy.Ecliptic(geo).elon;
        }

        const { gate, line } = lonToGateLine(lon);
        out.push({ ...planet, lon, gate, line });
      } catch (err) {
        console.error(`Calculation failed for ${planet.id}`, err);
      }
    });
    return out;
  }

  // --- Theme palette ------------------------------------------------------
  function readPalette() {
    const cs = getComputedStyle(document.documentElement);
    const get = (name, fallback) => {
      const v = cs.getPropertyValue(name).trim();
      return v || fallback;
    };
    return {
      accent: get("--accent", "#c4622d"),
      bg: get("--bg", "#faf8f5"),
      surface: get("--surface", "#ffffff"),
      text: get("--text", "#1a1613"),
      textMid: get("--text-mid", "#5c5550"),
      textSoft: get("--text-soft", "#8a837c"),
      border: get("--border", "#e2ddd6"),
      borderStrong: get("--border-strong", "#c9c2b8"),
    };
  }

  // Convert a hex/rgb color to an rgba() string with the given alpha.
  function withAlpha(color, alpha) {
    const c = color.trim();
    if (c.startsWith("#")) {
      let hex = c.slice(1);
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((ch) => ch + ch)
          .join("");
      }
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    if (c.startsWith("rgb")) {
      const nums = c
        .replace(/rgba?\(|\)/g, "")
        .split(",")
        .map((n) => n.trim());
      return `rgba(${nums[0]}, ${nums[1]}, ${nums[2]}, ${alpha})`;
    }
    return c;
  }

  // --- Widget -------------------------------------------------------------
  // `options.bare` is the wheel alone, with no date, time or search controls
  // and no astronomy-engine: the caller hands it the positions through the
  // returned `setPlanets`. That is how the knowledge base's live sky block
  // (/docs/widgets/sky.js) draws the sky from the engine's own change list, so
  // the site has one wheel and the live one matches the app to the minute.
  // Every mounted wheel, so a page that swaps articles without a load (the
  // in-app KB panel) can let go of the ones it removed: prune().
  const live = new Set();

  function prune() {
    live.forEach((handle) => {
      if (!handle.root.isConnected) handle.destroy();
    });
  }

  function createWidget(container, options) {
    const bare = !!(options && options.bare);
    // Avoid double-mounting.
    if (container.dataset.mandalaMounted === "1") return container.hdMandala || null;
    container.dataset.mandalaMounted = "1";

    const state = {
      date: new Date(),
      activePlanets: [],
      searchHighlight: null, // { type, value }
      palette: readPalette(),
    };

    // ----- Build DOM ------------------------------------------------------
    const root = document.createElement("div");
    root.className = "hd-mandala-widget";

    const controls = document.createElement("div");
    controls.className = "hd-mandala-controls";

    // Date input
    const dateGroup = document.createElement("div");
    dateGroup.className = "hd-mandala-group";
    const dateLabel = document.createElement("label");
    const dateId = `mandala-date-${Math.random().toString(36).slice(2, 8)}`;
    dateLabel.setAttribute("for", dateId);
    dateLabel.textContent = T("mandala.date", "Date");
    const dateInput = document.createElement("input");
    dateInput.type = "date";
    dateInput.id = dateId;
    dateInput.className = "hd-mandala-input";
    dateGroup.append(dateLabel, dateInput);

    // Day offset slider
    const offsetGroup = document.createElement("div");
    offsetGroup.className = "hd-mandala-group";
    const offsetLabel = document.createElement("label");
    const offsetId = `mandala-offset-${Math.random().toString(36).slice(2, 8)}`;
    offsetLabel.setAttribute("for", offsetId);
    const offsetText = document.createElement("span");
    offsetText.textContent = T("mandala.day_offset", "Day offset");
    const offsetVal = document.createElement("span");
    offsetVal.className = "hd-mandala-value";
    offsetVal.textContent = T("mandala.days_many", "{sign}{n} days", { sign: "", n: 0 });
    offsetLabel.append(offsetText, offsetVal);
    const offsetSlider = document.createElement("input");
    offsetSlider.type = "range";
    offsetSlider.id = offsetId;
    offsetSlider.min = "-20";
    offsetSlider.max = "20";
    offsetSlider.step = "1";
    offsetSlider.value = "0";
    offsetGroup.append(offsetLabel, offsetSlider);

    // Time-of-day slider
    const timeGroup = document.createElement("div");
    timeGroup.className = "hd-mandala-group";
    const timeLabel = document.createElement("label");
    const timeId = `mandala-time-${Math.random().toString(36).slice(2, 8)}`;
    timeLabel.setAttribute("for", timeId);
    const timeText = document.createElement("span");
    timeText.textContent = T("mandala.time_of_day", "Time of day");
    const timeVal = document.createElement("span");
    timeVal.className = "hd-mandala-value";
    timeVal.textContent = "12:00";
    timeLabel.append(timeText, timeVal);
    const timeSlider = document.createElement("input");
    timeSlider.type = "range";
    timeSlider.id = timeId;
    timeSlider.min = "0";
    timeSlider.max = "1439";
    timeSlider.step = "1";
    timeSlider.value = "720";
    timeGroup.append(timeLabel, timeSlider);

    // Search box
    const searchGroup = document.createElement("div");
    searchGroup.className = "hd-mandala-group";
    const searchLabel = document.createElement("label");
    const searchId = `mandala-search-${Math.random().toString(36).slice(2, 8)}`;
    searchLabel.setAttribute("for", searchId);
    searchLabel.textContent = T("mandala.search_label", "Search gate or planet");
    const searchInput = document.createElement("input");
    searchInput.type = "text";
    searchInput.id = searchId;
    searchInput.className = "hd-mandala-input";
    searchInput.placeholder = T("mandala.search_hint", "e.g. 25 or Mars");
    searchInput.autocomplete = "off";
    const searchResult = document.createElement("div");
    searchResult.className = "hd-mandala-result";
    searchResult.setAttribute("aria-live", "polite");
    searchGroup.append(searchLabel, searchInput, searchResult);

    controls.append(dateGroup, offsetGroup, timeGroup, searchGroup);

    // Canvas + expand button
    const stage = document.createElement("div");
    stage.className = "hd-mandala-stage";

    const expandBtn = document.createElement("button");
    expandBtn.type = "button";
    expandBtn.className = "hd-mandala-expand";
    expandBtn.setAttribute("aria-label", T("mandala.expand", "Expand the mandala to fullscreen"));
    expandBtn.textContent = "⤢";

    const canvas = document.createElement("canvas");
    canvas.className = "hd-mandala-canvas";
    canvas.setAttribute("role", "img");
    canvas.setAttribute(
      "aria-label",
      T(
        "mandala.canvas_aria",
        "Rave Mandala wheel showing the 64 Human Design gates and the current planetary positions with the channels they activate.",
      ),
    );

    // Brand lockup — only shown in the fullscreen view (see toggleFullscreen).
    const logo = document.createElement("div");
    logo.className = "hd-mandala-logo";
    logo.setAttribute("aria-hidden", "true");

    stage.append(expandBtn, canvas);
    if (bare) {
      root.classList.add("is-bare");
      root.append(stage, logo);
    } else root.append(controls, stage, logo);
    container.append(root);

    const ctx = canvas.getContext("2d");
    let width = 0;
    let height = 0;
    let centerX = 0;
    let centerY = 0;
    let outerRadius = 0;

    // ----- Drawing --------------------------------------------------------
    function channelCurve(radA, radB, innerR) {
      const ax = Math.cos(radA) * innerR;
      const ay = Math.sin(radA) * innerR;
      const bx = Math.cos(radB) * innerR;
      const by = Math.sin(radB) * innerR;

      const mx = (ax + bx) / 2;
      const my = (ay + by) / 2;
      const dist = Math.sqrt(mx * mx + my * my);
      const pullFactor = 0.35 + 0.5 * (1 - Math.min(dist / innerR, 1));

      return { ax, ay, bx, by, cx: mx * pullFactor, cy: my * pullFactor };
    }

    function drawChannels(innerR, activatedGates, fullscreen) {
      const p = state.palette;
      const searchGate =
        state.searchHighlight && state.searchHighlight.type === "gate" ? state.searchHighlight.value : null;

      // Pass 1: inactive faint channel lines — only in the expanded (fullscreen)
      // view; they make the small preview too busy.
      if (fullscreen) {
        CHANNELS.forEach(([gateA, gateB]) => {
          const isActive = activatedGates.has(gateA) && activatedGates.has(gateB);
          const isSearched = searchGate === gateA || searchGate === gateB;
          if (isActive || isSearched) return;

          const { ax, ay, bx, by, cx, cy } = channelCurve(
            degToCanvasRad(gateToAngleDeg(gateA)),
            degToCanvasRad(gateToAngleDeg(gateB)),
            innerR,
          );
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.quadraticCurveTo(cx, cy, bx, by);
          ctx.strokeStyle = withAlpha(p.borderStrong, 0.4);
          ctx.lineWidth = 1;
          ctx.stroke();
        });
      }

      // Pass 2: active / searched on top
      CHANNELS.forEach(([gateA, gateB]) => {
        const isActive = activatedGates.has(gateA) && activatedGates.has(gateB);
        const isSearched = searchGate === gateA || searchGate === gateB;
        if (!isActive && !isSearched) return;

        const { ax, ay, bx, by, cx, cy } = channelCurve(
          degToCanvasRad(gateToAngleDeg(gateA)),
          degToCanvasRad(gateToAngleDeg(gateB)),
          innerR,
        );
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.quadraticCurveTo(cx, cy, bx, by);
        if (isActive) {
          ctx.strokeStyle = p.accent;
          ctx.lineWidth = 2.5;
        } else {
          ctx.strokeStyle = withAlpha(p.accent, 0.5);
          ctx.lineWidth = 2;
        }
        ctx.stroke();
      });
    }

    function drawEarthCenter() {
      const p = state.palette;
      const earth = PLANETS.find((pl) => pl.id === "Earth");
      const col = earth ? earth.color : p.accent;
      ctx.beginPath();
      ctx.arc(0, 0, 13, 0, Math.PI * 2);
      ctx.fillStyle = withAlpha(col, 0.18);
      ctx.fill();
      ctx.strokeStyle = col;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-13, 0);
      ctx.lineTo(13, 0);
      ctx.moveTo(0, -13);
      ctx.lineTo(0, 13);
      ctx.stroke();
    }

    // A bare wheel on a phone (sky.js on a narrow page). Everything drawn on it
    // must fit a canvas ~330px across: 64 ring numbers there are 9px apart and
    // smear into each other, and labels pushed outward leave the canvas.
    function isCompact() {
      return bare && width < 460;
    }

    function drawGatesRing(rIn, rOut, activatedGates, fullscreen) {
      const p = state.palette;
      const compact = isCompact();
      // Compact: only the numbers of gates a body holds, and never two
      // neighbours (their numbers would touch); the body's label names its gate.
      const numbered = new Set();
      for (let i = 0; i < 64; i++) {
        const gateNum = GATE_SEQUENCE[i];
        const isActivated = activatedGates.has(gateNum);
        const isSearchHighlight =
          state.searchHighlight && state.searchHighlight.type === "gate" && state.searchHighlight.value === gateNum;

        const startDeg = GATE_41_START + i * GATE_ANGLE;
        const centerDeg = startDeg + GATE_ANGLE / 2;
        const radStart = degToCanvasRad(startDeg);
        const radEnd = degToCanvasRad(startDeg + GATE_ANGLE);

        if (isActivated || isSearchHighlight) {
          ctx.beginPath();
          ctx.arc(0, 0, rOut, radEnd, radStart);
          ctx.arc(0, 0, rIn, radStart, radEnd, true);
          ctx.closePath();
          ctx.fillStyle = isSearchHighlight ? withAlpha(p.accent, 0.45) : withAlpha(p.accent, 0.16);
          ctx.fill();
        }

        ctx.beginPath();
        ctx.moveTo(Math.cos(radStart) * rIn, Math.sin(radStart) * rIn);
        ctx.lineTo(Math.cos(radStart) * rOut, Math.sin(radStart) * rOut);
        ctx.strokeStyle = withAlpha(p.borderStrong, 0.6);
        ctx.lineWidth = 1;
        ctx.stroke();

        if (compact) {
          const free = !numbered.has((i + 63) % 64) && !(i === 63 && numbered.has(0));
          if (!(isActivated || isSearchHighlight) || !free) continue;
          numbered.add(i);
        }

        const centerRad = degToCanvasRad(centerDeg);
        const textR = rIn + (rOut - rIn) / 2;

        ctx.save();
        ctx.translate(Math.cos(centerRad) * textR, Math.sin(centerRad) * textR);
        let normDeg = centerDeg % 360;
        if (normDeg < 0) normDeg += 360;
        let rot = centerRad + Math.PI / 2;
        if (normDeg > 90 && normDeg < 270) rot += Math.PI;
        ctx.rotate(rot);

        if (isSearchHighlight) ctx.fillStyle = p.accent;
        else ctx.fillStyle = isActivated ? p.text : p.textSoft;
        const gateFontSize = fullscreen ? 11 : 9;
        ctx.font = `${isActivated || isSearchHighlight ? "bold " : ""}${gateFontSize}px "DM Sans", system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(gateNum), 0, 0);
        ctx.restore();
      }

      ctx.strokeStyle = withAlpha(p.borderStrong, 0.6);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(0, 0, rOut, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, rIn, 0, Math.PI * 2);
      ctx.stroke();
    }

    const LABEL_FONT = '11px "DM Sans", system-ui, sans-serif';

    function rectsOverlap(x1, y1, w1, h1, x2, y2, w2, h2) {
      return Math.abs(x1 - x2) < (w1 + w2) / 2 + 4 && Math.abs(y1 - y2) < (h1 + h2) / 2 + 2;
    }

    function drawPlanets(innerRad) {
      const p = state.palette;
      const labelFont = LABEL_FONT;
      ctx.font = labelFont;

      // On a narrow bare wheel the names would run off the canvas; the list
      // beside it (sky.js) names every body, so the rim keeps glyph and gate.
      const compact = isCompact();
      const labelData = state.activePlanets.map((pl) => {
        const displayName = pl.label || pl.id;
        const text = compact
          ? `${pl.symbol} ${pl.gate}.${pl.line}`
          : `${pl.symbol} ${displayName} ${pl.gate}.${pl.line}`;
        const w = ctx.measureText(text).width + 14;
        return { ...pl, text, w, h: 18 };
      });

      const LABEL_BASE_OFFSET = 30;
      const LABEL_STEP = 10;
      const MAX_ATTEMPTS = 24;
      const placedLabels = [];
      // A label beside the wheel (at 3 or 9 o'clock) is wide across the ring:
      // keep its nearest corner off the gate numbers.
      const clearOfRing = (x, y, w, h) =>
        Math.hypot(Math.max(Math.abs(x) - w / 2, 0), Math.max(Math.abs(y) - h / 2, 0)) >= outerRadius + 4;

      // Compact: every label stays inside the canvas. Candidates close to the
      // rim first, then nudged along it, then a little further out; the first
      // one inside the edge and clear of the others wins.
      const half = width / 2 - 2;
      const inside = (x, y, w, h) => Math.abs(x) + w / 2 <= half && Math.abs(y) + h / 2 <= half;
      const COMPACT_OFFSETS = [20, 34, 48, 62];
      const COMPACT_NUDGES = [0];
      for (let k = 1; k <= 7; k++) COMPACT_NUDGES.push(k * 0.08, -k * 0.08);
      function placeCompact(pl) {
        const pRad = degToCanvasRad(pl.lon);
        for (const offset of COMPACT_OFFSETS) {
          for (const nudge of COMPACT_NUDGES) {
            const r = outerRadius + offset;
            const x = Math.cos(pRad + nudge) * r;
            const y = Math.sin(pRad + nudge) * r;
            if (!inside(x, y, pl.w, pl.h)) continue;
            if (placedLabels.some((prev) => rectsOverlap(x, y, pl.w, pl.h, prev.x, prev.y, prev.w, prev.h))) continue;
            placedLabels.push({ x, y, w: pl.w, h: pl.h });
            return { ...pl, pRad, moved: offset > 20 || nudge !== 0, textX: x, textY: y };
          }
        }
        // Nothing clear: the rim position, pulled inside the edge.
        const r = outerRadius + 20;
        const clamp = (v, w) => Math.max(-half + w / 2, Math.min(half - w / 2, v));
        const x = clamp(Math.cos(pRad) * r, pl.w);
        const y = clamp(Math.sin(pRad) * r, pl.h);
        placedLabels.push({ x, y, w: pl.w, h: pl.h });
        return { ...pl, pRad, moved: true, textX: x, textY: y };
      }

      // Straight out first; then, when a neighbour holds that spot (two bodies
      // a degree apart), slid along the rim before giving up on the ring.
      const RIM_NUDGES = [0];
      for (let k = 1; k <= 8; k++) RIM_NUDGES.push(k * 0.05, -k * 0.05);
      const labelPlacements = labelData.map((pl) => {
        if (compact) return placeCompact(pl);
        const pRad = degToCanvasRad(pl.lon);
        for (const nudge of RIM_NUDGES) {
          for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            const labelOffset = LABEL_BASE_OFFSET + attempt * LABEL_STEP;
            const r = outerRadius + labelOffset;
            const x = Math.cos(pRad + nudge) * r;
            const y = Math.sin(pRad + nudge) * r;
            // Pushed past the edge, a name is cut off ("upiter 4.5" on a phone).
            if (!inside(x, y, pl.w, pl.h)) break;
            if (!clearOfRing(x, y, pl.w, pl.h)) continue;
            if (placedLabels.some((prev) => rectsOverlap(x, y, pl.w, pl.h, prev.x, prev.y, prev.w, prev.h))) continue;
            placedLabels.push({ x, y, w: pl.w, h: pl.h });
            return { ...pl, pRad, moved: labelOffset > LABEL_BASE_OFFSET || nudge !== 0, textX: x, textY: y };
          }
        }

        // No room further out: find it closer in, the way a compact wheel does.
        return placeCompact(pl);
      });

      labelPlacements.forEach((pl) => {
        const isSearchHighlight =
          state.searchHighlight && state.searchHighlight.type === "planet" && state.searchHighlight.value === pl.id;

        // Spoke from center to inner ring.
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(pl.pRad) * innerRad, Math.sin(pl.pRad) * innerRad);
        ctx.strokeStyle = pl.color;
        ctx.globalAlpha = isSearchHighlight ? 1 : 0.5;
        ctx.lineWidth = isSearchHighlight ? 2 : 1;
        ctx.setLineDash([2, 4]);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;

        // Arrow pointing out to the gate.
        const arrowTipR = outerRadius + 5;
        const arrowBaseR = outerRadius + 18;
        const tipX = Math.cos(pl.pRad) * arrowTipR;
        const tipY = Math.sin(pl.pRad) * arrowTipR;
        const wingLen = 9;

        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(
          tipX + Math.cos(pl.pRad + Math.PI - 0.2) * wingLen,
          tipY + Math.sin(pl.pRad + Math.PI - 0.2) * wingLen,
        );
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(
          tipX + Math.cos(pl.pRad + Math.PI + 0.2) * wingLen,
          tipY + Math.sin(pl.pRad + Math.PI + 0.2) * wingLen,
        );
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(Math.cos(pl.pRad) * arrowBaseR, Math.sin(pl.pRad) * arrowBaseR);
        ctx.strokeStyle = pl.color;
        ctx.lineWidth = isSearchHighlight ? 3 : 2;
        ctx.stroke();

        // Label pill.
        ctx.save();
        ctx.translate(pl.textX, pl.textY);
        ctx.font = labelFont;

        ctx.fillStyle = isSearchHighlight ? withAlpha(p.accent, 0.18) : withAlpha(p.surface, 0.92);
        ctx.beginPath();
        // Safari before 16 has no roundRect; a square pill still reads.
        if (ctx.roundRect) ctx.roundRect(-pl.w / 2, -pl.h / 2, pl.w, pl.h, 4);
        else ctx.rect(-pl.w / 2, -pl.h / 2, pl.w, pl.h);
        ctx.fill();
        ctx.strokeStyle = isSearchHighlight ? p.accent : withAlpha(p.border, 0.9);
        ctx.lineWidth = isSearchHighlight ? 1.5 : 1;
        ctx.stroke();

        // Colored dot.
        ctx.beginPath();
        ctx.arc(-pl.w / 2 + 8, 0, 3, 0, Math.PI * 2);
        ctx.fillStyle = pl.color;
        ctx.fill();

        ctx.fillStyle = p.text;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(pl.text, 5, 0);
        ctx.restore();

        // Dotted connector when the label was pushed out.
        if (pl.moved) {
          const from = compact ? outerRadius + 18 : outerRadius + LABEL_BASE_OFFSET - 8;
          ctx.beginPath();
          ctx.moveTo(Math.cos(pl.pRad) * from, Math.sin(pl.pRad) * from);
          ctx.lineTo(pl.textX - Math.cos(pl.pRad) * 10, pl.textY - Math.sin(pl.pRad) * 10);
          ctx.strokeStyle = withAlpha(pl.color, 0.6);
          ctx.lineWidth = 1;
          ctx.setLineDash([1, 2]);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      });
    }

    function draw() {
      if (!width || !height) return;
      ctx.clearRect(0, 0, width, height);

      const midRad = outerRadius * 0.85;
      const innerRad = midRad - outerRadius * 0.12;
      const fullscreen = root.classList.contains("is-fullscreen");

      const activatedGates = new Set(state.activePlanets.map((pl) => pl.gate));

      ctx.save();
      ctx.translate(centerX, centerY);
      drawChannels(midRad, activatedGates, fullscreen);
      drawEarthCenter();
      drawGatesRing(midRad, outerRadius, activatedGates, fullscreen);
      drawPlanets(innerRad);
      ctx.restore();
    }

    // ----- Sizing ---------------------------------------------------------
    function resizeCanvas() {
      const dpr = window.devicePixelRatio || 1;
      const fs = root.classList.contains("is-fullscreen");
      let size;
      if (fs) {
        size = Math.min(stage.clientWidth, stage.clientHeight) - 16;
      } else {
        const avail = stage.clientWidth || container.clientWidth || 320;
        size = Math.min(avail, 560);
      }
      size = Math.max(size, 240);

      width = size;
      height = size;
      canvas.width = Math.round(size * dpr);
      canvas.height = Math.round(size * dpr);
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      centerX = size / 2;
      centerY = size / 2;
      // Leave room for the planet labels around the rim.
      // A narrow bare wheel (a phone) gives its compact labels a little more.
      outerRadius = size * (bare && size < 460 ? 0.31 : 0.34);
      if (!isCompact()) {
        // The widest name must fit between the ring and the edge beside it.
        ctx.font = LABEL_FONT;
        const widest = Math.max(...PLANETS.map((pl) => ctx.measureText(`${pl.symbol} ${pl.label} 64.6`).width + 14));
        outerRadius = Math.max(size * 0.27, Math.min(outerRadius, size / 2 - widest - 24));
      }

      draw();
    }

    // ----- Input handling -------------------------------------------------
    function runSearch() {
      const query = searchInput.value.trim().toLowerCase();
      state.searchHighlight = null;

      if (!query) {
        searchResult.textContent = "";
        return;
      }

      // Gate number.
      const gateNum = parseInt(query, 10);
      if (
        !Number.isNaN(gateNum) &&
        gateNum >= 1 &&
        gateNum <= 64 &&
        GATE_SEQUENCE.includes(gateNum) &&
        String(gateNum) === query
      ) {
        state.searchHighlight = { type: "gate", value: gateNum };
        const activating = state.activePlanets.filter((pl) => pl.gate === gateNum);
        const activatedGates = new Set(state.activePlanets.map((pl) => pl.gate));
        const connected = CHANNELS.filter(([a, b]) => a === gateNum || b === gateNum);
        const activeChannels = connected.filter(([a, b]) => activatedGates.has(a) && activatedGates.has(b));

        const name = GATE_NAMES[gateNum] || "";
        let text = T("mandala.result_gate", "Gate {gate}: {name}", { gate: gateNum, name: name });
        text =
          activating.length > 0
            ? T("mandala.result_activated", "{text} — activated by {list}", {
                text: text,
                list: activating.map((pl) => pl.label || pl.id).join(", "),
              })
            : T("mandala.result_not_activated", "{text} — not activated", { text: text });
        if (activeChannels.length > 0) {
          text = T("mandala.result_channels", "{text} | Channels: {list}", {
            text: text,
            list: activeChannels.map(([a, b]) => `${a}-${b}`).join(", "),
          });
        }
        searchResult.textContent = text;
        return;
      }

      // Planet name / symbol.
      const planet = state.activePlanets.find(
        (pl) =>
          pl.id.toLowerCase().includes(query) ||
          (pl.label && pl.label.toLowerCase().includes(query)) ||
          pl.symbol === query ||
          (query === "node" && (pl.id === "NorthNode" || pl.id === "SouthNode")),
      );
      if (planet) {
        state.searchHighlight = { type: "planet", value: planet.id };
        const name = GATE_NAMES[planet.gate] || "";
        searchResult.textContent = T("mandala.result_planet", "{symbol} {label} → Gate {gate}.{line} ({name})", {
          symbol: planet.symbol,
          label: planet.label || planet.id,
          gate: planet.gate,
          line: planet.line,
          name: name,
        });
        return;
      }

      searchResult.textContent = T("mandala.no_match", "No match found");
    }

    function recompute() {
      const dateStr = dateInput.value;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return;

      const dayOffset = parseInt(offsetSlider.value, 10);
      const totalMinutes = parseInt(timeSlider.value, 10);

      const sign = dayOffset > 0 ? "+" : "";
      offsetVal.textContent = T(
        Math.abs(dayOffset) === 1 ? "mandala.days_one" : "mandala.days_many",
        `{sign}{n} day${Math.abs(dayOffset) !== 1 ? "s" : ""}`,
        { sign: sign, n: dayOffset },
      );

      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      // Military time + a plain-language hint so users aren't confused by 24h
      // notation. midnight=00:00, noon=12:00 get words; everything else AM/PM.
      const hint =
        hours === 0 && mins === 0
          ? T("mandala.midnight", "midnight")
          : hours === 12 && mins === 0
            ? T("mandala.noon", "noon")
            : `${hours % 12 || 12}${mins === 0 ? "" : ":" + String(mins).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}`;
      timeVal.textContent = `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")} (${hint})`;

      const [year, month, day] = dateStr.split("-");
      const baseDate = new Date(year, month - 1, day, hours, mins, 0);
      baseDate.setDate(baseDate.getDate() + dayOffset);
      state.date = baseDate;

      state.activePlanets = calculatePositions(state.date);
      runSearch();
      draw();
    }

    // ----- Fullscreen -----------------------------------------------------
    let closeBtn = null;
    function toggleFullscreen() {
      const entering = !root.classList.contains("is-fullscreen");
      root.classList.toggle("is-fullscreen", entering);
      document.body.classList.toggle("hd-mandala-noscroll", entering);

      // Only one button at a time: expand in the preview, close in fullscreen.
      expandBtn.style.display = entering ? "none" : "";

      // Fill the brand lockup the first time we go fullscreen (logo.js is loaded
      // by then). Grey secondary lockup reads well on the neutral overlay.
      if (entering && !logo.dataset.filled && typeof window.logoGreyWithTextSVG === "function") {
        logo.innerHTML = window.logoGreyWithTextSVG({ height: 26 });
        logo.dataset.filled = "1";
      }

      if (entering && !closeBtn) {
        closeBtn = document.createElement("button");
        closeBtn.type = "button";
        closeBtn.className = "hd-mandala-close";
        closeBtn.setAttribute("aria-label", T("mandala.close_fullscreen", "Close fullscreen"));
        closeBtn.textContent = "✕";
        closeBtn.addEventListener("click", toggleFullscreen);
        root.append(closeBtn);
      }
      if (closeBtn) closeBtn.style.display = entering ? "" : "none";
      // Next frame so layout settles before measuring.
      requestAnimationFrame(resizeCanvas);
    }

    // ----- Init -----------------------------------------------------------
    if (!bare) {
      const now = new Date();
      const tzOffset = now.getTimezoneOffset() * 60000;
      dateInput.value = new Date(now.getTime() - tzOffset).toISOString().split("T")[0];

      dateInput.addEventListener("input", recompute);
      offsetSlider.addEventListener("input", recompute);
      timeSlider.addEventListener("input", recompute);
      searchInput.addEventListener("input", () => {
        runSearch();
        draw();
      });
    }
    expandBtn.addEventListener("click", toggleFullscreen);
    const onKey = (e) => {
      if (e.key === "Escape" && root.classList.contains("is-fullscreen")) {
        toggleFullscreen();
      }
    };
    document.addEventListener("keydown", onKey);

    // Re-read palette + redraw when the theme toggles.
    const themeObserver = new MutationObserver(() => {
      state.palette = readPalette();
      draw();
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    // Responsive resize.
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => resizeCanvas());
      ro.observe(stage);
    } else {
      window.addEventListener("resize", resizeCanvas);
    }

    resizeCanvas();
    if (!bare) recompute();

    // Positions from the caller: [{ id: "Sun" | "NorthNode" | …, gate, line, lon }].
    const handle = {
      setPlanets(list) {
        state.activePlanets = list
          .map((p) => {
            const planet = PLANETS.find((pl) => pl.id === p.id);
            return planet ? { ...planet, lon: p.lon, gate: p.gate, line: p.line } : null;
          })
          .filter(Boolean);
        draw();
      },
      // Lets go of everything outside the wheel that holds on to it.
      destroy() {
        document.removeEventListener("keydown", onKey);
        themeObserver.disconnect();
        if (ro) ro.disconnect();
        else window.removeEventListener("resize", resizeCanvas);
        live.delete(handle);
      },
      root: root,
    };
    container.hdMandala = handle;
    live.add(handle);
    return handle;
  }

  // --- Boot ---------------------------------------------------------------
  // `root` is the in-app panel's article (kb-panel.js); a doc page boots the
  // whole document. Each wheel mounts once, whichever caller gets there first.
  function boot(root) {
    const containers = Array.prototype.filter.call(
      (root || document).querySelectorAll("[data-mandala]"),
      (el) => !el.hdMandala,
    );
    if (!containers.length) return;

    const start = () => {
      containers.forEach((el) => el.hdMandala || createWidget(el));
    };

    if (typeof Astronomy !== "undefined") {
      start();
      return;
    }
    // astronomy-engine loads via defer from a CDN; poll until ready.
    let tries = 0;
    const check = setInterval(() => {
      tries += 1;
      if (typeof Astronomy !== "undefined") {
        clearInterval(check);
        start();
      } else if (tries > 100) {
        clearInterval(check);
        console.error("Mandala: astronomy-engine failed to load.");
      }
    }, 100);
  }

  window.GHD_MANDALA = { mount: createWidget, boot: boot, prune: prune };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }
})();
